import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  CircularProgress,
  FormControlLabel,
  Switch,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { GetMasterReport, DownloadPolicyFilesZip } from "../../Service/_index";
import { ToastError, ToastSuccess } from "../Toaster/Toast";

// Written into the zip by the backend when a policy has no downloadable file.
const SKIPPED_FILE_NAME = "_skipped-policies.txt";

/**
 * Turn a policy number into a safe file name.
 * Slashes become dashes ("POL/123/456" -> "POL-123-456"). The master report
 * prefixes policy numbers with an apostrophe so Excel keeps them as text, so
 * that is stripped first. Other characters the filesystem rejects are replaced.
 */
export const policyNumberToFileName = (policyNumber) => {
  const base = (policyNumber == null ? "" : String(policyNumber))
    .trim()
    .replace(/^['`]+/, "")
    .trim();
  if (!base) return "policy";
  return (
    base
      .replace(/[/\\]/g, "-")
      .replace(/[:*?"<>|]/g, "-")
      .replace(/\s+/g, " ")
      .replace(/-+/g, "-")
      .trim() || "policy"
  );
};

const formatBytes = (bytes) => {
  if (!bytes) return "0 KB";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const StatCard = ({ label, value, color }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 92,
      border: "1px solid #E4E7EC",
      borderRadius: "8px",
      p: 1.5,
      textAlign: "center",
      backgroundColor: "#fff",
    }}
  >
    <Typography
      sx={{ fontSize: 22, fontWeight: 700, color: color || "#1D2939" }}
    >
      {value}
    </Typography>
    <Typography sx={{ fontSize: 12, color: "#667085", fontFamily: "Inter" }}>
      {label}
    </Typography>
  </Box>
);

/**
 * Download the policy PDFs for the CURRENT filters of the calling page.
 *
 * The policy list is loaded once (all filtered pages), then ALL files are
 * fetched in a SINGLE request — the backend streams one zip. Cancelling aborts
 * that request. The zip is either saved as-is or unpacked in the browser when
 * the user prefers individual files, so either way it is one API call.
 *
 * Props:
 *  - open / onClose        dialog control.
 *  - loadRows              optional `async () => [{ _id, policyNumber }]`. Pages
 *                          with their own list endpoint (CC Entry) pass this;
 *                          without it the master-report list is loaded from
 *                          apiReqBody / requestType / totalCount.
 *  - statuses              optional policy statuses to allow in the zip, e.g.
 *                          ["approvedPolicy", "approvePending"]. The backend
 *                          defaults to approved-only when this is omitted.
 *  - apiReqBody            master-report filter body (master-report path only).
 *  - requestType           "MASTER" | "SCORE" (master-report path only).
 *  - totalCount            filtered row count, used as the page size.
 */
const PolicyFileDownloadDialog = ({
  open,
  onClose,
  loadRows,
  statuses,
  apiReqBody,
  requestType,
  totalCount,
}) => {
  const [rows, setRows] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState("");
  const [asZip, setAsZip] = useState(true);
  // idle | downloading | saving | done
  const [status, setStatus] = useState("idle");
  const [receivedBytes, setReceivedBytes] = useState(0);
  const [savedCount, setSavedCount] = useState(0);
  const [skippedList, setSkippedList] = useState([]);
  const [note, setNote] = useState("");

  const abortRef = useRef(null);
  // The caller's loader closes over its filter state, so a new function
  // identity arrives on every parent render. Keeping it in a ref lets the load
  // effect stay keyed on `open` instead of re-running in a loop.
  const loadRowsRef = useRef(loadRows);
  loadRowsRef.current = loadRows;

  const total = rows.length;
  const isBusy = status === "downloading" || status === "saving";

  const resetRun = useCallback(() => {
    setStatus("idle");
    setReceivedBytes(0);
    setSavedCount(0);
    setSkippedList([]);
    setNote("");
  }, []);

  // Load the full filtered list whenever the dialog is opened.
  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    const loadList = async () => {
      resetRun();
      setRows([]);
      setListError("");
      setLoadingList(true);
      try {
        let tableData = [];
        if (loadRowsRef.current) {
          // The page knows its own list endpoint and filter shape.
          tableData = (await loadRowsRef.current()) || [];
        } else {
          // Normal (paginated) path with a big page size — it returns _id, which
          // the Excel export path does not.
          const body = {
            ...(apiReqBody || {}),
            isDownloadExcel: false,
            page: 1,
            limit: Math.max(Number(totalCount) || 0, 1000),
          };
          const res = await GetMasterReport(requestType || "MASTER", body);
          tableData = res?.data?.tableData || [];
        }
        if (cancelled) return;
        const list = tableData
          .map((item) => ({
            _id: item?._id,
            policyNumber: item?.policyNumber || "",
          }))
          .filter((item) => item._id);
        setRows(list);
        if (list.length === 0) {
          setListError(
            tableData.length > 0
              ? `Loaded ${tableData.length} row(s) but none had a policy id — cannot fetch their files.`
              : "No policies found for the current filters. Run a search first, then try again.",
          );
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Error loading policy list:", error);
        setListError("Failed to load the filtered policy list.");
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    };

    loadList();
    return () => {
      cancelled = true;
      if (abortRef.current) abortRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, apiReqBody, requestType, totalCount, resetRun]);

  const handleStart = async () => {
    if (!rows.length) return;
    resetRun();
    setStatus("downloading");
    setNote("Preparing files on the server...");

    const controller = new AbortController();
    abortRef.current = controller;

    const res = await DownloadPolicyFilesZip(
      rows.map((r) => r._id),
      {
        statuses,
        signal: controller.signal,
        onDownloadProgress: (evt) => {
          setReceivedBytes(evt?.loaded || 0);
          if (evt?.loaded) setNote("");
        },
      },
    );

    abortRef.current = null;

    if (res?.cancelled) {
      setStatus("idle");
      setNote("");
      ToastError("Download cancelled");
      return;
    }
    if (res?.error || !res?.blob) {
      setStatus("idle");
      setNote("");
      ToastError(res?.message || "Failed to download policy files");
      return;
    }

    setStatus("saving");
    setNote("Reading the downloaded archive...");
    try {
      // Open the archive to report exactly what came back (and to unpack it when
      // the user wants individual files).
      const zip = await JSZip.loadAsync(res.blob);
      const entries = Object.values(zip.files).filter((f) => !f.dir);
      const pdfEntries = entries.filter((f) => f.name !== SKIPPED_FILE_NAME);

      const skippedEntry = zip.file(SKIPPED_FILE_NAME);
      if (skippedEntry) {
        const text = await skippedEntry.async("string");
        setSkippedList(
          text
            .split("\n")
            .map((l) => l.trim())
            .filter((l) => l && !l.toLowerCase().startsWith("these ")),
        );
      }

      if (pdfEntries.length === 0) {
        setStatus("done");
        setNote("");
        ToastError("None of the selected policies had a downloadable file");
        return;
      }

      if (asZip) {
        saveAs(res.blob, `Policy-Files-${pdfEntries.length}.zip`);
        setSavedCount(pdfEntries.length);
        ToastSuccess(`Saved ${pdfEntries.length} policy file(s) as ZIP`);
      } else {
        setNote("Saving files individually...");
        let saved = 0;
        for (const entry of pdfEntries) {
          const fileBlob = await entry.async("blob");
          saveAs(fileBlob, entry.name);
          saved += 1;
          setSavedCount(saved);
          // Browsers throttle bursts of automatic downloads.
          await new Promise((r) => setTimeout(r, 250));
        }
        ToastSuccess(`Downloaded ${saved} policy file(s)`);
      }
      setStatus("done");
      setNote("");
    } catch (error) {
      console.error("Error reading the archive:", error);
      setStatus("idle");
      setNote("");
      ToastError("The downloaded archive could not be read");
    }
  };

  const handleCancel = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  const handleClose = () => {
    if (abortRef.current) abortRef.current.abort();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={isBusy ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: "bold",
          color: "#374151",
        }}
      >
        Download Policy Files
        <Tooltip title={isBusy ? "Cancel first to close" : "Close"}>
          <span>
            <IconButton size="small" onClick={handleClose} disabled={isBusy}>
              <CloseIcon />
            </IconButton>
          </span>
        </Tooltip>
      </DialogTitle>

      <DialogContent dividers>
        {loadingList ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 3 }}>
            <CircularProgress size={22} />
            <Typography variant="body2">
              Loading the filtered policy list...
            </Typography>
          </Box>
        ) : listError ? (
          <Alert severity="warning">{listError}</Alert>
        ) : (
          <>
            <Typography variant="body2" sx={{ color: "#667085", mb: 2 }}>
              All policies matching your current filters (every page). Their
              files are fetched in a <strong>single request</strong>.
            </Typography>

            <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
              <StatCard label="Total" value={total} />
              <StatCard label="Downloaded" value={savedCount} color="#12B76A" />
              <StatCard
                label="Skipped"
                value={skippedList.length}
                color="#F04438"
              />
            </Box>

            {isBusy && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress
                  variant={
                    status === "downloading" && !receivedBytes
                      ? "indeterminate"
                      : status === "saving" && !asZip && total
                        ? "determinate"
                        : "indeterminate"
                  }
                  value={
                    status === "saving" && !asZip && total
                      ? Math.round((savedCount / total) * 100)
                      : undefined
                  }
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: "#667085", mt: 0.5, display: "block" }}
                >
                  {note ||
                    (status === "downloading"
                      ? `Received ${formatBytes(receivedBytes)}...`
                      : `Saving ${savedCount} of ${total}...`)}
                </Typography>
              </Box>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={asZip}
                  onChange={(e) => setAsZip(e.target.checked)}
                  disabled={isBusy}
                />
              }
              label={
                <Typography variant="body2">
                  Save as a single ZIP file
                </Typography>
              }
            />
            <Typography
              variant="caption"
              sx={{ display: "block", color: "#667085", mb: 1 }}
            >
              {asZip
                ? "One .zip is saved — recommended for large sets."
                : "The archive is unpacked in your browser and each PDF is saved separately. Your browser may ask to allow multiple downloads."}
            </Typography>

            <Alert severity="info" sx={{ mt: 1 }}>
              Files are named by policy number, with <strong>/</strong> replaced
              by <strong>-</strong> (e.g. <code>POL/123/456</code> →{" "}
              <code>POL-123-456.pdf</code>).
            </Alert>

            {status === "done" && skippedList.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {skippedList.length} policy file(s) were not available (no PDF
                uploaded for those policies).
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, px: 3 }}>
        <Box sx={{ flex: 1 }} />
        {isBusy ? (
          <Button onClick={handleCancel} color="error" variant="outlined">
            Cancel
          </Button>
        ) : (
          <Button onClick={handleClose} color="inherit">
            Close
          </Button>
        )}
        <Button
          onClick={handleStart}
          variant="contained"
          disabled={loadingList || isBusy || rows.length === 0}
          startIcon={
            isBusy ? <CircularProgress size={18} color="inherit" /> : null
          }
        >
          {status === "downloading"
            ? "Downloading..."
            : status === "saving"
              ? "Saving..."
              : status === "done"
                ? `Download Again (${total})`
                : `Download All (${total})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PolicyFileDownloadDialog;
